import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Skeleton } from "../ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  getResourceVariants,
  deleteResourceVariant,
  regenerateVariant,
  listStudents,
  generateResourceVariants,
} from "../../lib/api";
import {
  Users,
  UserCircle,
  Eye,
  RefreshCw,
  Trash2,
  Loader2,
  FileText,
  Calendar,
  AlertTriangle,
  Check,
  Plus,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { formatRelativeTime, NiveauBadge } from "./constants";

function VariantCard({ variant, onView, onRegenerate, onDelete, isRegenerating }) {
  const readingAids = [];
  if (variant.student_preferences?.colored_syllables) readingAids.push("Syllabes");
  if (variant.student_preferences?.show_silent_letters) readingAids.push("Lettres muettes");
  if (variant.student_preferences?.line_alternation) readingAids.push("Alternance");

  return (
    <TooltipProvider>
      <div
        data-testid={`variant-card-${variant.id}`}
        className="flex items-center gap-3 p-3 bg-white border rounded-xl hover:shadow-sm transition-all"
      >
        {/* Student avatar */}
        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <UserCircle className="w-6 h-6 text-indigo-600" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-slate-800">
              {variant.student_name || "Élève inconnu"}
            </span>
            {variant.student_niveau && (
              <NiveauBadge niveau={variant.student_niveau} className="text-[10px]" />
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
            <Calendar className="w-3 h-3" />
            <span>{formatRelativeTime(variant.created_at)}</span>
            {readingAids.length > 0 && (
              <>
                <span className="text-slate-300">|</span>
                <span className="text-indigo-600">{readingAids.join(", ")}</span>
              </>
            )}
          </div>
        </div>

        {/* Status */}
        <Badge
          variant={variant.status === "ready" ? "default" : "secondary"}
          className="text-[10px]"
        >
          {variant.status === "ready" ? (
            <><Check className="w-3 h-3 mr-1" /> Prêt</>
          ) : (
            <><AlertTriangle className="w-3 h-3 mr-1" /> En attente</>
          )}
        </Badge>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => onView(variant)}
              >
                <Eye className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Voir la variante</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => onRegenerate(variant)}
                disabled={isRegenerating}
              >
                {isRegenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Régénérer</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                onClick={() => onDelete(variant)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Supprimer</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  );
}

export function VariantsViewDialog({ open, onOpenChange, resource }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [variants, setVariants] = useState([]);
  const [students, setStudents] = useState([]);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open && resource) {
      fetchData();
    }
  }, [open, resource]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [variantsRes, studentsRes] = await Promise.all([
        getResourceVariants(resource.id),
        listStudents(),
      ]);
      setVariants(variantsRes.data.variants || []);
      setStudents(studentsRes.data || []);
    } catch (err) {
      console.error("Error fetching variants:", err);
      toast.error("Erreur lors du chargement des variantes");
    } finally {
      setLoading(false);
    }
  };

  const handleViewVariant = (variant) => {
    // Navigate to student resource viewer
    navigate(`/student/resource/${variant.id}`);
  };

  const handleRegenerateVariant = async (variant) => {
    setRegeneratingId(variant.id);
    try {
      await regenerateVariant(variant.id);
      toast.success("Variante régénérée !");
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la régénération");
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleDeleteVariant = async (variant) => {
    if (!confirm(`Supprimer la variante de ${variant.student_name} ?`)) return;
    
    try {
      await deleteResourceVariant(resource.id, variant.id);
      toast.success("Variante supprimée");
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleGenerateForAll = async () => {
    // Get students without variants
    const existingStudentIds = variants.map(v => v.target_student_id);
    const targetStudentIds = resource.target?.student_ids || [];
    const missingStudentIds = targetStudentIds.filter(id => !existingStudentIds.includes(id));
    
    if (missingStudentIds.length === 0) {
      toast.info("Toutes les variantes sont déjà générées");
      return;
    }
    
    setGenerating(true);
    try {
      await generateResourceVariants(resource.id, missingStudentIds);
      toast.success(`${missingStudentIds.length} variante(s) générée(s)`);
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  // Calculate missing variants
  const targetStudentIds = resource?.target?.student_ids || [];
  const existingStudentIds = variants.map(v => v.target_student_id);
  const missingCount = targetStudentIds.filter(id => !existingStudentIds.includes(id)).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            Variantes de "{resource?.title}"
          </DialogTitle>
          <DialogDescription>
            Gérez les versions différenciées de cette ressource pour chaque élève
          </DialogDescription>
        </DialogHeader>

        {/* Summary */}
        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-600">
              <strong>{variants.length}</strong> variante{variants.length > 1 ? "s" : ""} générée{variants.length > 1 ? "s" : ""}
            </span>
          </div>
          {missingCount > 0 && (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {missingCount} manquante{missingCount > 1 ? "s" : ""}
            </Badge>
          )}
          <div className="flex-1" />
          {missingCount > 0 && (
            <Button
              size="sm"
              variant="default"
              onClick={handleGenerateForAll}
              disabled={generating}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-1" />
              )}
              Générer les manquantes
            </Button>
          )}
        </div>

        {/* Variants list */}
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="space-y-2 p-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : variants.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Users className="w-12 h-12 mb-3" />
              <p className="text-lg font-medium">Aucune variante générée</p>
              <p className="text-sm mt-1">
                Cliquez sur "Générer les manquantes" pour créer les versions différenciées
              </p>
            </div>
          ) : (
            <div className="space-y-2 p-2">
              {variants.map((variant) => (
                <VariantCard
                  key={variant.id}
                  variant={variant}
                  onView={handleViewVariant}
                  onRegenerate={handleRegenerateVariant}
                  onDelete={handleDeleteVariant}
                  isRegenerating={regeneratingId === variant.id}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
